if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
          closeButtonFloating: 'sticky-atc-bar__close--floating',
          closeButtonVisible: 'sticky-atc-bar__close--show',
        };

        this.submitEl.addEventListener('click', this.onClick.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      get closeButton() {
        return (
          this._closeButton ||
          this.querySelector('[data-sticky-atc-close]') ||
          document.querySelector(`[data-sticky-atc-close][data-sticky-atc-section="${this.dataset.section}"]`)
        );
      }

      get storageKey() {
        return `sticky-atc-bar-dismissed-${this.dataset.productId || 'product'}`;
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.isDismissed = this.getDismissedState();
        this.setupCloseButton();

        this.init();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this.boundUpdateCloseButtonPosition) {
          window.removeEventListener('resize', this.boundUpdateCloseButtonPosition);
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      init() {
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          this.updateCloseButton(false);
          return;
        }

        if (this.isDismissed) {
          this.hideDismissedBar();
          return;
        }

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (this.isDismissed) {
                this.hideDismissedBar();
                return;
              }
              const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle(this.classes.isOpen, isAboveViewport);
              this.updateCloseButton(isAboveViewport);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      setupCloseButton() {
        this._closeButton = this.closeButton;
        if (!this._closeButton) return;

        this._closeButton.classList.add(this.classes.closeButtonFloating);
        document.body.appendChild(this._closeButton);

        if (!this.closeButtonClickBound) {
          this._closeButton.addEventListener('click', this.dismiss.bind(this));
          this.closeButtonClickBound = true;
        }

        this.boundUpdateCloseButtonPosition = this.updateCloseButtonPosition.bind(this);
        window.addEventListener('resize', this.boundUpdateCloseButtonPosition);
      }

      updateCloseButton(show) {
        if (!this._closeButton) return;

        if (show && !this.isDismissed) {
          this._closeButton.classList.add(this.classes.closeButtonVisible);
          this.updateCloseButtonPosition();
          requestAnimationFrame(() => this.updateCloseButtonPosition());
        } else {
          this._closeButton.classList.remove(this.classes.closeButtonVisible);
        }
      }

      updateCloseButtonPosition() {
        if (!this._closeButton || !this.classList.contains(this.classes.isOpen)) return;

        const rect = this.getBoundingClientRect();
        const buttonSize = this._closeButton.offsetWidth || 28;
        const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
        const top = isMobile
          ? Math.max(0, window.innerHeight - this.offsetHeight - buttonSize - 5)
          : Math.max(0, rect.top - buttonSize - 5);
        const insetInlineEnd = isMobile ? 0 : Math.max(0, window.innerWidth - rect.right);

        this._closeButton.style.top = `${top}px`;
        this._closeButton.style.insetInlineEnd = `${insetInlineEnd}px`;
      }

      onClick() {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      getDismissedState() {
        try {
          return sessionStorage.getItem(this.storageKey) === 'true';
        } catch (error) {
          return false;
        }
      }

      setDismissedState() {
        try {
          sessionStorage.setItem(this.storageKey, 'true');
        } catch (error) {
          // Session storage can be unavailable in some privacy modes.
        }
      }

      hideDismissedBar() {
        this.classList.remove(this.classes.isOpen);
        this.classList.add('sticky-atc-bar--dismissed');
        this.updateCloseButton(false);
      }

      dismiss() {
        this.isDismissed = true;
        this.setDismissedState();
        this.hideDismissedBar();
        if (this.observer) this.observer.disconnect();
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCartShort;
        }
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          this.updatePrice(variant);
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}
